import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown, ChevronLeft, Info, Search, Users, X,
  User, FolderClosed, Pin, PinOff, Heart, Ban,
} from 'lucide-react';
import ProfilePhotoModal from './ProfilePhotoModal';

const ChatHeader = ({
  selectedUser,
  typingUsers,
  employeeProfilePics,
  onBack,
  // null hides the info button entirely (e.g. the Mini Chat Dock, which has
  // no contact-info panel to open); MessengerPage always passes its own
  // handler explicitly, so this default never affects it.
  onInfoClick = null,

  isRTL,
  c,
  styles,
  getAvatarColor,
  t,
  isMobile,
  // Live presence override. `selectedUser.online` was captured at the moment
  // the user was selected and never updates afterwards, so the header sat at
  // "Offline" even after user:online events flipped the actual state. When
  // this prop is provided we trust it instead.
  isOnline,
  activeMembersCount,
  // WhatsApp-style header extras
  displayName,             // alias-aware name (falls back to selectedUser name)
  onToggleSearch = null,   // opens the in-chat search bar
  onOpenMediaDocs = null,  // opens the media/links/docs page
  onTogglePin = null,
  isPinned = false,
  onToggleFavorite = null,
  isFavorite = false,
  onToggleBlock = null,
  isBlocked = false,
}) => {
  const showOnline = typeof isOnline === 'boolean' ? isOnline : !!selectedUser.online;
  const isGroup = String(selectedUser.id).startsWith('group-') || selectedUser.isGroup;
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null); // toggle button's DOMRect at open time
  const [menuPos, setMenuPos] = useState(null); // {top, left} in viewport coords, clamped
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);

  // The header lives inside a `overflow: hidden` chat column, so an absolutely
  // positioned dropdown gets clipped below the messages list. Render it in a
  // portal with fixed coordinates measured from the toggle button instead.
  const toggleMenu = () => {
    setMenuOpen((prev) => {
      const next = !prev;
      if (next && menuBtnRef.current) {
        setAnchorRect(menuBtnRef.current.getBoundingClientRect());
      }
      return next;
    });
  };

  // Compute + clamp against the ACTUAL rendered menu size, exactly like
  // MessageActionsMenu.jsx. The previous implementation positioned the menu
  // with a raw `right: window.innerWidth - r.right` offset — in RTL layouts
  // the toggle button sits near the LEFT edge of the viewport, so that offset
  // could exceed the viewport width and push the whole menu off-screen to
  // the left (invisible), which is exactly the bug reported in Arabic/RTL.
  // Using a clamped `left` instead makes this direction-agnostic.
  useLayoutEffect(() => {
    if (!menuOpen || !anchorRect) { setMenuPos(null); return; }
    const MARGIN = 8;
    const menuWidth = menuRef.current?.offsetWidth || 230;
    const menuHeight = menuRef.current?.offsetHeight || 260;

    let left = anchorRect.right - menuWidth;
    left = Math.min(Math.max(left, MARGIN), window.innerWidth - menuWidth - MARGIN);

    let top = anchorRect.bottom + 6;
    top = Math.min(Math.max(top, MARGIN), window.innerHeight - menuHeight - MARGIN);

    setMenuPos({ top, left });
  }, [menuOpen, anchorRect]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        menuBtnRef.current && !menuBtnRef.current.contains(e.target)
      ) setMenuOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const headerName = displayName || selectedUser.englishName || selectedUser.name;

  const iconButtonStyle = {
    width: '42px',
    height: '42px',
    borderRadius: '14px',
    border: `1px solid ${c.border}`,
    background: c.inputBg,
    color: c.text,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 0.16s ease, background 0.16s ease',
  };

  // Ordered exactly per the WhatsApp-style spec: view contact, media/links/
  // docs, pin, favorite, block. Contact-specific rows hidden for groups.
  const menuItems = [
    ...(onInfoClick
      ? [{ icon: <User size={16} />, label: isGroup ? t.contactInfo : t.viewContact, onClick: onInfoClick }]
      : []),
    ...(onOpenMediaDocs ? [{ icon: <FolderClosed size={16} />, label: t.mediaLinksDocs, onClick: onOpenMediaDocs }] : []),
    ...(onTogglePin ? [{
      icon: isPinned ? <PinOff size={16} /> : <Pin size={16} />,
      label: isPinned ? t.unpinChat : t.pinChat,
      onClick: onTogglePin,
    }] : []),
    ...(onToggleFavorite ? [{
      icon: <Heart size={16} fill={isFavorite ? '#ef4444' : 'none'} color={isFavorite ? '#ef4444' : undefined} />,
      label: isFavorite ? t.removeFromFavorites : t.addToFavorites,
      onClick: onToggleFavorite,
    }] : []),
    ...(!isGroup && onToggleBlock ? [{
      icon: <Ban size={16} />,
      label: isBlocked ? t.unblockAction : t.blockAction,
      onClick: onToggleBlock,
      danger: !isBlocked,
    }] : []),
  ];

  return (
    <div style={styles.chatHeader}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack} style={styles.backButton}>
            <ChevronLeft style={{ width: '22px', height: '22px', transform: isRTL ? 'scaleX(-1)' : 'none' }} />
          </button>

          <div
            style={{ position: 'relative', cursor: 'pointer' }}
            onClick={() => setShowPhotoModal(true)}
            title={isRTL ? 'عرض الصورة' : 'View photo'}
          >
            {employeeProfilePics[selectedUser.id] ? (
              <img
                src={employeeProfilePics[selectedUser.id]}
                alt={headerName}
                style={{
                  ...styles.avatar,
                  width: '48px',
                  height: '48px',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling?.style) e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}

            <div
              style={{
                ...styles.avatar,
                width: '48px',
                height: '48px',
                fontSize: '18px',
                background: getAvatarColor(selectedUser.name),
                display: employeeProfilePics[selectedUser.id] ? 'none' : 'flex',
              }}
            >
              {String(selectedUser.id).startsWith('group-') ? <Users size={24} /> : selectedUser.avatar}
            </div>
            {showOnline && <div style={styles.onlineIndicator} />}
          </div>

          <div>
            <h2
              style={{
                fontWeight: '600',
                color: c.text,
                margin: 0,
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {headerName}
              {isPinned && <Pin size={13} color={c.textSecondary} />}
            </h2>
            <p
              style={{
                fontSize: '13px',
                color: c.textSecondary,
                margin: 0,
              }}
            >
              {typingUsers.has(selectedUser.id) ? (
                <span style={{ fontStyle: 'italic', color: '#2563eb' }}>{t.typing}</span>
              ) : isGroup ? (
                activeMembersCount > 0 ? (
                  <span style={{ color: '#10b981' }}>{activeMembersCount} {isRTL ? 'نشط' : 'Active'}</span>
                ) : (
                  <span style={{ color: c.textSecondary }}>{selectedUser.members?.length || 0} {isRTL ? 'أعضاء' : 'Members'}</span>
                )
              ) : (
                showOnline ? t.activeNow : t.offline
              )}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: isRTL ? '0' : 'auto', marginRight: isRTL ? 'auto' : '0' }}>
          {onToggleSearch && (
            <button
              type="button"
              onClick={onToggleSearch}
              style={iconButtonStyle}
              title={t.searchInChat}
              aria-label={t.searchInChat}
            >
              <Search size={18} />
            </button>
          )}

          {/* <button
            type="button"
            className="chat-info-btn"
            onClick={onInfoClick}
            style={iconButtonStyle}
            title={t.contactInfo}
            aria-label={t.contactInfo}
          >
            <Info size={18} />
          </button> */}

          {/* Chat options dropdown (rendered in a portal to escape overflow).
              Hidden entirely when there's nothing to show — e.g. the Mini
              Chat Dock passes none of the optional handlers below. */}
          {menuItems.length > 0 && (
            <>
              <button
                ref={menuBtnRef}
                type="button"
                onClick={toggleMenu}
                style={{ ...iconButtonStyle, ...(menuOpen ? { borderColor: '#2e7cba', color: '#2e7cba' } : {}) }}
                title={t.moreOptions}
                aria-label={t.moreOptions}
                aria-expanded={menuOpen}
              >
                <ChevronDown size={18} style={{ transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
              </button>
              {menuOpen && createPortal(
                <div
                  ref={menuRef}
                  style={{
                    position: 'fixed',
                    // First paint happens off-screen purely to measure
                    // offsetWidth/offsetHeight (see the useLayoutEffect
                    // above) — becomes visible once the clamped position
                    // is known, so it never flashes at the wrong spot.
                    top: menuPos ? menuPos.top : -9999,
                    left: menuPos ? menuPos.left : -9999,
                    visibility: menuPos ? 'visible' : 'hidden',
                    zIndex: 100000,
                    minWidth: '230px',
                    background: c.popoverBg || c.sidebarBg,
                    border: `1px solid ${c.popoverBorder || c.border}`,
                    borderRadius: '16px',
                    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.22)',
                    padding: '8px',
                    animation: 'popoverSlideUp 0.15s ease',
                    direction: isRTL ? 'rtl' : 'ltr',
                  }}
                >
                  {menuItems.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => { setMenuOpen(false); item.onClick(); }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        border: 'none',
                        background: 'transparent',
                        color: item.danger ? '#ef4444' : c.text,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '13.5px',
                        fontWeight: '600',
                        textAlign: 'start',
                      }}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </>
          )}

          {/* Exit chat — back to the "no chat selected" empty state.
              Redundant on mobile: the ChevronLeft backButton at the start of
              this header already does the same thing there. */}
          {!isMobile && (
            <button
              type="button"
              onClick={onBack}
              style={iconButtonStyle}
              title={t.exitChat}
              aria-label={t.exitChat}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {showPhotoModal && (
        <ProfilePhotoModal
          imageUrl={employeeProfilePics[selectedUser.id]}
          name={headerName}
          fallbackColor={getAvatarColor(selectedUser.name)}
          isGroup={isGroup}
          onClose={() => setShowPhotoModal(false)}
        />
      )}
    </div>
  );
};

export default ChatHeader;
